import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Autoplay } from 'swiper';
import Link from '../Link';
import { useClassName, useWindowSize } from 'common/hooks';
import { ContentfulRichText, ExternalLink } from 'common/components';
import HomepageAsset from '../Asset';
import HomepageImage from 'public/screens/Home/Components/Image';
import googleMaps from 'common/assets/google-maps.svg';
import appleMaps from 'common/assets/apple-maps.svg';

import useCustomStyle from '../../hooks';
import useSlider from '../../../Home/Components/useSlider';

import './header.less';
import 'swiper/swiper.less';
import 'swiper/modules/effect-fade/effect-fade.less';
import { getStylesByPrefix } from '../../utils';

function TypeAHeader({ component }) {
  const classNames = useClassName('CLPHeader');
  const [setRef] = useCustomStyle(component.fields.settings);

  return (
    <div
      className={classNames(['type-a', component.fields.theme || 'light'])}
      ref={setRef}>
      <div>
        {component.fields.subtitle && <span>{component.fields.subtitle}</span>}
        <h3>{component.fields.title}</h3>
      </div>
      <ContentfulRichText field={{ 'en-US': component.fields.description }} />
    </div>
  );
}

function TypeBHeader({ component, refs, handleTrack }) {
  const classNames = useClassName('CLPHeader');
  const [setRef, styles] = useCustomStyle(component.fields.settings);
  const titleStyles = getStylesByPrefix('--title-', styles);

  return (
    <div
      className={classNames(['type-b', component.fields.theme || 'light'])}
      ref={setRef}>
      <h3 style={titleStyles}>{component.fields.title}</h3>
      {component.fields.link && (
        <Link
          component={component.fields.link}
          refs={refs}
          handleTrack={handleTrack}
        />
      )}
    </div>
  );
}

function TypeCHeader({ component }) {
  const classNames = useClassName('CLPHeader');
  const [setRef] = useCustomStyle(component.fields.settings);

  return (
    <div
      className={classNames(['type-c', component.fields.theme || 'light'])}
      ref={setRef}>
      <h3>{component.fields.title}</h3>
    </div>
  );
}

function TypeDHeader({ component }) {
  const classNames = useClassName('CLPHeader');
  const [setRef, styles] = useCustomStyle(component.fields.settings);

  return (
    <div
      className={classNames(['type-d', component.fields.theme || 'light'])}
      ref={setRef}
      style={{
        ...(styles?.['--align'] && {
          alignItems: styles['--align'],
          display: 'flex',
          flexDirection: 'column',
        }),
      }}>
      <h3>{component.fields.title}</h3>
      {component?.fields?.subtitle && <h4>{component.fields.subtitle}</h4>}
    </div>
  );
}

function TypeEHeader({ component }) {
  const classNames = useClassName('CLPHeaderTypeE');
  const [setRef] = useCustomStyle(component.fields.settings);
  const [props] = useSlider();

  return (
    <div
      className={classNames(['container', component.fields.theme || 'light'])}
      ref={setRef}>
      <div>
        {Boolean(component?.fields?.tags?.length) && (
          <div className={classNames('tags')}>
            {component.fields.tags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        )}
        {Boolean(component.fields.subtitle) && (
          <h4>{component.fields.subtitle}</h4>
        )}
        {Boolean(component.fields.title) && <h3>{component.fields.title}</h3>}

        {Boolean(component.fields.description) && (
          <p>
            <ContentfulRichText
              field={{ 'en-US': component.fields.description }}
            />
          </p>
        )}
        {Boolean(component.fields.icon && component.fields.iconText) && (
          <span>
            <HomepageImage field={component.fields.icon} />
            <span>{component.fields.iconText}</span>
          </span>
        )}
      </div>
      <div className={classNames('slider')}>
        <Swiper
          {...(component.fields.assets.length > 1 && {
            modules: [EffectFade, Autoplay],
            effect: 'fade',
            fadeEffect: {
              crossFade: true,
            },
            autoplay: {
              delay: 5000,
            },
            loopAdditionalSlides: component.fields.assets.length,
            loop: true,
          })}
          centeredSlides
          slidesPerView="auto"
          {...props}>
          {component.fields.assets.map((asset, index) => (
            <SwiperSlide key={index}>
              <HomepageAsset
                key={asset.sys.id}
                className={classNames('item')}
                asset={asset}
                settings={component.fields.settings}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}

function TypeFHeader({ component }) {
  const classNames = useClassName('CLPHeader');
  const [setRef, styles] = useCustomStyle(component.fields.settings);

  return (
    <div
      className={classNames(['type-f', component.fields.theme || 'light'])}
      ref={setRef}>
      {Boolean(component.fields.subtitle) && (
        <h4
          {...(Boolean(styles['--secondary-color']) && {
            style: {
              color: styles['--secondary-color'],
            },
          })}>
          {component.fields.subtitle}
        </h4>
      )}
      <h3>{component.fields.title}</h3>
      {Boolean(component.fields.description) && (
        <ContentfulRichText field={{ 'en-US': component.fields.description }} />
      )}
    </div>
  );
}

function TypeGHeader({ component }) {
  const classNames = useClassName('CLPHeaderTypeG');
  const [setRef] = useCustomStyle(component.fields.settings);

  return (
    <div
      className={classNames(['type-g', component.fields.theme || 'light'])}
      ref={setRef}>
      <div className={classNames('content')}>
        <div className={classNames('title')}>{component.fields.title}</div>
        <div className={classNames('subtitle')}>
          {component.fields.subtitle}
        </div>
      </div>
    </div>
  );
}

function TypeHHeader({ component, handleTrack }) {
  const classNames = useClassName('CLPHeaderTypeH');
  const [setRef, styles] = useCustomStyle(component.fields.settings);
  const { isMobile } = useWindowSize();
  const googleUrl = styles['--google-url'];
  const appleUrl = styles['--apple-url'];

  const renderLink = (url, icon, text) =>
    url && (
      <ExternalLink
        href={url}
        className={classNames('button')}
        handleTrack={handleTrack}>
        <img src={icon} alt={text} />
        {text}
      </ExternalLink>
    );

  return (
    <div className={classNames('container')} ref={setRef}>
      <div className={classNames('content')}>
        <div className={classNames('title')}>{component.fields.title}</div>
        <div className={classNames('subtitle')}>
          {component.fields.subtitle}
        </div>
        <div className={classNames('description')}>
          <ContentfulRichText
            field={{ 'en-US': component.fields.description }}
          />
        </div>
      </div>
      <div className={classNames('img-container')}>
        <HomepageAsset
          key={component.fields.assets[0].sys.id}
          className={classNames('image')}
          asset={component.fields.assets[0]}
          settings={component.fields.settings}
        />
        <div className={classNames('button-overlay')}>
          {renderLink(googleUrl, googleMaps, 'View on Google Maps')}
          {renderLink(appleUrl, appleMaps, 'View on Apple Maps')}
        </div>
      </div>
      {isMobile && (
        <>
          <div className={classNames('description-mobile')}>
            <ContentfulRichText
              field={{ 'en-US': component.fields.description }}
            />
          </div>
          <div className={classNames('mobile-buttons')}>
            {renderLink(googleUrl, googleMaps, 'View on Google Maps')}
            {renderLink(appleUrl, appleMaps, 'View on Apple Maps')}
          </div>
        </>
      )}
    </div>
  );
}

function TypeIHeader({ component }) {
  const classNames = useClassName('CLPHeaderTypeI');
  const [setRef] = useCustomStyle(component.fields.settings);
  const { isMobile } = useWindowSize();

  return (
    <div className={classNames('container')} ref={setRef}>
      <div
        className={classNames(['content', component.fields.theme || 'light'])}>
        <div className={classNames('title')}>{component.fields.title}</div>
        {component.fields.subtitle && (
          <div className={classNames('subtitle')}>
            {component.fields.subtitle}
          </div>
        )}
        {component.fields.description && (
          <div className={classNames('description')}>
            <ContentfulRichText
              field={{ 'en-US': component.fields.description }}
            />
          </div>
        )}
      </div>
      {isMobile && (
        <>
          <div className={classNames('description-mobile')}>
            <ContentfulRichText
              field={{ 'en-US': component.fields.description }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function TypeJHeader({ component, refs, handleTrack }) {
  const classNames = useClassName('CLPHeaderTypeJ');
  const [setRef, styles] = useCustomStyle(component.fields.settings);

  return (
    <div className={classNames('container')} ref={setRef}>
      <div
        className={classNames([
          'text-content',
          component.fields.theme || 'light',
        ])}>
        <div
          className={classNames('title')}
          style={getStylesByPrefix('--title-', styles)}>
          {component.fields.title}
        </div>
        {component.fields.subtitle && (
          <div className={classNames('subtitle')}>
            {component.fields.subtitle}
          </div>
        )}

        {component.fields.description && (
          <div className={classNames('description')}>
            <ContentfulRichText
              field={{ 'en-US': component.fields.description }}
            />
          </div>
        )}
      </div>
      {component.fields.link && (
        <Link
          component={component.fields.link}
          refs={refs}
          handleTrack={handleTrack}
        />
      )}
    </div>
  );
}
function TypeKHeader({ component }) {
  const classNames = useClassName('CLPHeader');
  const [setRef, styles] = useCustomStyle(component.fields.settings);
  const textAlign =
    styles['--align'] === 'center'
      ? 'center'
      : styles['--align'] === 'flex-start'
      ? 'left'
      : 'auto';

  return (
    <div
      className={classNames(['type-k', component.fields.theme || 'light'])}
      ref={setRef}
      style={{
        ...(styles?.['--align'] && {
          alignItems: styles['--align'],
        }),
      }}>
      <div className={classNames('type-k__title')}>
        {component.fields.subtitle && (
          <span
            style={{
              ...(styles?.['--subtitle-font-size'] && {
                fontSize: styles['--subtitle-font-size'],
              }),
              textAlign,
            }}>
            {component.fields.subtitle}
          </span>
        )}
        <h3
          style={{
            ...(styles?.['--title-font-size'] && {
              fontSize: styles['--title-font-size'],
            }),
            ...(styles?.['--align'] && {
              textAlign,
            }),
          }}>
          {component.fields.title}
        </h3>
      </div>
      <div
        className={classNames('type-k__description')}
        style={{
          ...(styles?.['--description-font-size'] && {
            fontSize: styles['--description-font-size'],
          }),
          ...(styles?.['--description-text-align'] && {
            textAlign: styles['--description-text-align'],
          }),
        }}>
        <ContentfulRichText field={{ 'en-US': component.fields.description }} />
      </div>
    </div>
  );
}

function TypeLHeader({ component, refs, handleTrack }) {
  const classNames = useClassName('CLPHeader');
  const [setRef, styles] = useCustomStyle(component.fields.settings);
  const image = component.fields.assets ? component.fields.assets[0] : null;
  const image1 = component.fields.assets ? component.fields.assets[1] : null;
  const isAbsoluteImage =
    styles['--image-position'] && styles['--image-position'] === 'absolute';
  const assetPosition = styles['--asset-position'] || undefined; // "top" or "bottom"
  const textAlign = styles['--align'] || undefined;
  const link = component.fields.link;
  const useSecondaryLink = styles['--use-secondary-link'] === 'true' || false;
  const mainStyles = Object.fromEntries(
    Object.entries(styles)
      .filter(([key]) => key.startsWith('--main'))
      .map(([key, value]) => [key.replace('--main-', ''), value])
  );
  const ctaStyle = getStylesByPrefix('--cta-', styles);

  // Applies any `--content-main-*` setting as a real CSS property on the
  // content main element (e.g. `--content-main-flex-direction: row`). Uses
  // setProperty so hyphenated property names work, unlike a React inline
  // style object.
  const setContentMainRef = (node) => {
    if (!node) return;
    Object.entries(styles)
      .filter(([key]) => key.startsWith('--content-main-'))
      .forEach(([key, value]) => {
        if (value == null) return;
        node.style.setProperty(
          key.replace('--content-main-', ''),
          value,
          'important'
        );
      });
  };

  const gradientStyles = {
    position: 'absolute',
    width: '100%',
    left: 0,
    bottom: '-40px',
    ...getStylesByPrefix('--gradient-', styles),
  };

  const gap = styles?.['--gap'] || '80px';
  const wrapperStyles = getStylesByPrefix('--wrapper-', styles);

  const renderAsset = (asset, suffix) => {
    if (!asset) return null;
    const display = styles[`--image-display${suffix}`] || undefined;
    if (display === 'none') return null;
    const position = styles[`--image-position${suffix}`];
    const isAbsolute = position === 'absolute';

    return (
      <div
        className={classNames('type-l__asset', isAbsolute ? 'absolute' : '')}>
        <HomepageAsset
          key={asset.sys.id}
          className={classNames('image')}
          asset={asset}
          settings={component.fields.settings}
          style={{
            borderRadius: styles[`--image-border-radius${suffix}`],
            ...(styles[`--image-width${suffix}`] && {
              width: styles[`--image-width${suffix}`],
            }),
            ...(styles[`--image-height${suffix}`] && {
              height: styles[`--image-height${suffix}`],
            }),
            ...(position && {
              position,
              zIndex: styles[`--image-z-index${suffix}`] || -1,
            }),
            ...(styles[`--image-top${suffix}`] && {
              top: styles[`--image-top${suffix}`],
            }),
            ...(styles[`--image-left${suffix}`] && {
              left: styles[`--image-left${suffix}`],
            }),
            ...(styles[`--image-right${suffix}`] && {
              right: styles[`--image-right${suffix}`],
            }),
            ...(styles[`--image-bottom${suffix}`] && {
              bottom: styles[`--image-bottom${suffix}`],
            }),
            ...(display && { display }),
          }}
        />
      </div>
    );
  };

  return (
    <div
      style={{
        textAlign,
        gap: !isAbsoluteImage ? gap : '0px',
        ...wrapperStyles,
      }}
      className={classNames([
        'type-l',
        component.fields.theme || 'light',
        image && !isAbsoluteImage && assetPosition
          ? `type-l__with-asset-${assetPosition}`
          : '',
      ])}
      ref={setRef}>
      <div className={classNames('type-l__content')}>
        <div style={gradientStyles} />
        <div
          className={classNames('type-l__content__main')}
          style={mainStyles}
          ref={setContentMainRef}>
          {component.fields.subtitle && (
            <span>{component.fields.subtitle}</span>
          )}
          <h3
            style={Object.fromEntries(
              Object.entries(styles)
                .filter(([key]) => key.startsWith('--title'))
                .map(([key, value]) => [key.replace('--title-', ''), value])
            )}>
            {component.fields.title}
          </h3>
          {component.fields.description && (
            <div
              className={classNames('type-l__description')}
              style={Object.fromEntries(
                Object.entries(styles)
                  .filter(([key]) => key.startsWith('--description'))
                  .map(([key, value]) => [
                    key.replace('--description-', ''),
                    value,
                  ])
              )}>
              <ContentfulRichText
                field={{ 'en-US': component.fields.description }}
              />
            </div>
          )}
          {image && link && !useSecondaryLink && (
            <Link
              component={component.fields.link}
              refs={refs}
              handleTrack={handleTrack}
              style={ctaStyle}
            />
          )}
        </div>
        {image && link && useSecondaryLink && (
          <Link
            component={component.fields.link}
            refs={refs}
            handleTrack={handleTrack}
            style={ctaStyle}
          />
        )}
      </div>

      {renderAsset(image, '')}
      {renderAsset(image1, '1')}
      {!image && !image1 && link && (
        <Link
          component={component.fields.link}
          refs={refs}
          handleTrack={handleTrack}
          style={ctaStyle}
        />
      )}
    </div>
  );
}

export default function Header({ component, refs, handleTrack }) {
  const Component = {
    'Type A': TypeAHeader,
    'Type B': TypeBHeader,
    'Type C': TypeCHeader,
    'Type D': TypeDHeader,
    'Type E': TypeEHeader,
    'Type F': TypeFHeader,
    'Type G': TypeGHeader,
    'Type H': TypeHHeader,
    'Type I': TypeIHeader,
    'Type J': TypeJHeader,
    'Type K': TypeKHeader,
    'Type L': TypeLHeader,
  }[component.fields.type];

  if (!Component) return null;

  return (
    <Component component={component} refs={refs} handleTrack={handleTrack} />
  );
}
